<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import {
  getFileLimits,
  SigningFileType,
  type ContractManagementContractFileInDb,
  type ProviderFileLimit,
  type SigningProvider,
  type VaultFileInDb,
  type VaultFileInDbSlim,
  type VaultFileRevisionInDb,
} from '@/api';
import GaiaBanner from '../composite/GaiaBanner.vue';
import GaiaDivider from '../base/GaiaDivider.vue';
import GaiaEnumSelect from '../base/selects/GaiaEnumSelect.vue';
import GaiaSectionHeader from '../base/GaiaSectionHeader.vue';
import GaiaSegmented, { type GaiaSegmentedOption } from '../base/GaiaSegmented.vue';
import GaiaSelectedDocRow from './GaiaSelectedDocRow.vue';
import GaiaText from '../base/GaiaText.vue';
import VaultFileBrowser from '@/components/file_vault/VaultFileBrowser.vue';
import type { VaultBrowserItem } from '@/components/file_vault/vaultFileBrowser';
import { ContractFilesService } from '@/services/contractManagement/contractFilesService';
import GaiaVaultUploadPanel from '../domain/fileVault/GaiaVaultUploadPanel.vue';
import type { VaultUploadPanelApi } from '../domain/fileVault/vaultUploadPanel';
import GaiaUploadButton from '../base/GaiaUploadButton.vue';
import { buildDocumentFromContractFile } from '@/composables/useSigningWizard';
import type {
  SignatureUploadContractSelectionState,
  SignatureUploadDocumentForm,
  SignatureUploadEffectiveFile,
  SignatureUploadForm,
} from '@/types/contractManagement/signatureFlow';
import { extractErrorDetail } from '@/utils/errorHandler';
import {
  buildEffectiveFileFromRevision,
  formatFileSize,
  getFileLimitViolationReason,
} from '@/utils/signatureFileValidation';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';
import GaiaDeleteButton from '../base/GaiaDeleteButton.vue';
import { gaiaNeutral } from '../themes/tokens';

/**
 * The prepare-signature wizard's first step: pick the files and say which of
 * them the signers actually sign. The request details ride along in #aside.
 *
 * Two sources behind one tab row — the vault, or an upload. The vault browser
 * shows folders, contracts and plain files: ticking a contract takes all of
 * its eligible files, opening it lets single files be ticked, and a plain file
 * is taken as it is. The browser only reports toggles; what a pick means is
 * decided here. Turning a pick into a
 * `SignatureUploadDocumentForm`, and deciding which files the signing provider
 * will accept at all, happens here: the provider picked in the request details (same step) sets the
 * file limits, and a file that stops qualifying is dropped from the selection
 * with a notice rather than failing later at activation.
 */

type Source = 'vault' | 'upload';

const props = defineProps({
  form: {
    type: Object as PropType<SignatureUploadForm>,
    required: true,
  },
  /** The request's provider — it decides the file limits. */
  provider: {
    type: String as PropType<SigningProvider | undefined>,
    default: undefined,
  },
  /** Contract context when the wizard was opened from a contract. */
  currentContractUid: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ 'update:form': [form: SignatureUploadForm] }>();

const source = ref<Source>('upload');

const sourceTabs = computed<GaiaSegmentedOption[]>(() => [
  { label: 'Upload', value: 'upload', tourKey: 'signing.doc-tab-upload' },
  { label: 'Vault', value: 'vault', tourKey: 'signing.doc-tab-file' },
]);

// ── Provider file limits ────────────────────────────────────────────────────

const providerFileLimits = ref<ProviderFileLimit | null>(null);
const providerFileLimitsLoading = ref(false);
const providerFileLimitsError = ref<string | null>(null);
const fileLimitNotice = ref<string | null>(null);
const fileLimitRequestVersion = ref(0);

const acceptedExtensions = computed(
  () =>
    providerFileLimits.value?.allowed_file_extensions
      .map(extension => extension.trim().replace(/^\./, '').toLowerCase())
      .filter(extension => extension.length > 0) ?? []
);

const uploadAccept = computed(() =>
  acceptedExtensions.value.length > 0
    ? acceptedExtensions.value.map(extension => `.${extension}`).join(',')
    : undefined
);

/**
 * The one eligibility rule, applied to whichever revision a picker hands over.
 * Ordered so the reason names the thing the user can act on first.
 */
function revisionReason(revision: VaultFileRevisionInDb | null | undefined): string | null {
  if (!props.provider) return 'Select a signing provider to validate eligible files';
  if (providerFileLimitsLoading.value) return 'Loading the provider’s file limits';
  if (providerFileLimitsError.value) return providerFileLimitsError.value;
  if (!revision) return 'No current revision';

  return getFileLimitViolationReason(
    buildEffectiveFileFromRevision(revision),
    providerFileLimits.value
  );
}

function contractFileDisabledReason(file: ContractManagementContractFileInDb): string | null {
  return revisionReason(file.vault_file?.current_revision);
}

/** For the selected-documents list and the auto-drop watcher below. */
function limitViolationReason(
  vaultFile: VaultFileInDbSlim,
  effectiveFile?: SignatureUploadEffectiveFile | null
): string | null {
  if (effectiveFile) {
    return getFileLimitViolationReason(effectiveFile, providerFileLimits.value);
  }

  const revision = vaultFile.current_revision;
  if (!revision) return 'File has no current revision.';

  return getFileLimitViolationReason(
    buildEffectiveFileFromRevision(revision),
    providerFileLimits.value
  );
}

// ── Selection → form ────────────────────────────────────────────────────────

function isContractFileDocument(
  document: SignatureUploadDocumentForm
): document is SignatureUploadDocumentForm & {
  source: {
    kind: 'contract-file';
    contract_uid: string;
    contract_name: string | null;
    contract_file_uid: string;
  };
} {
  return document.source?.kind === 'contract-file';
}

function haveSameStringSet(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;

  const leftSet = new Set(left);
  return right.every(value => leftSet.has(value));
}

/**
 * One row per vault file. A file reachable both through its contract and
 * through the vault keeps its contract identity, so the row can still say
 * where it came from.
 */
function normalizeDocuments(
  documents: SignatureUploadDocumentForm[]
): SignatureUploadDocumentForm[] {
  const byVaultFileUid = new Map<string, SignatureUploadDocumentForm>();

  for (const document of documents) {
    const existing = byVaultFileUid.get(document.vault_file.uid);
    if (!existing || (isContractFileDocument(document) && !isContractFileDocument(existing))) {
      byVaultFileUid.set(document.vault_file.uid, document);
    }
  }

  return [...byVaultFileUid.values()];
}

/** Keeps the contract selection in step with the documents that survive. */
function resolveContractSelection(
  documents: SignatureUploadDocumentForm[],
  preferred: SignatureUploadContractSelectionState | null
): SignatureUploadContractSelectionState | null {
  const selected = documents.filter(isContractFileDocument);
  if (selected.length === 0) return null;

  const contractUids = new Set(selected.map(document => document.source.contract_uid));
  if (contractUids.size !== 1) return null;

  const firstSource = selected[0].source;
  const base: SignatureUploadContractSelectionState = {
    contract_uid: firstSource.contract_uid,
    contract_name: firstSource.contract_name,
    contract_file_uids: selected.map(document => document.source.contract_file_uid),
    mode: 'contract-files',
  };

  const keepsPreferredMode =
    preferred &&
    preferred.contract_uid === base.contract_uid &&
    haveSameStringSet(preferred.contract_file_uids, base.contract_file_uids);

  return keepsPreferredMode ? { ...base, mode: preferred.mode } : base;
}

function updateForm(
  documents: SignatureUploadDocumentForm[],
  preferredContractSelection: SignatureUploadContractSelectionState | null = props.form
    .contract_selection ?? null
): void {
  const nextDocuments = normalizeDocuments(documents);

  emit('update:form', {
    documents: nextDocuments,
    contract_selection: resolveContractSelection(nextDocuments, preferredContractSelection),
  });
}

function updateDocumentType(index: number, signingFileType: SigningFileType): void {
  updateForm(
    props.form.documents.map((document, currentIndex) =>
      currentIndex === index ? { ...document, signing_file_type: signingFileType } : document
    )
  );
}

function removeDocument(index: number): void {
  updateForm(props.form.documents.filter((_, currentIndex) => currentIndex !== index));
}

// ── Contract files ──────────────────────────────────────────────────────────

const contractError = ref<string | null>(null);
const skippedNotice = ref<string | null>(null);

const lockedContract = computed(() => {
  const selection = props.form.contract_selection;
  return selection ? { uid: selection.contract_uid, name: selection.contract_name } : null;
});

async function toggleContractFile(
  contract: { uid: string; name: string },
  file: ContractManagementContractFileInDb
): Promise<void> {
  contractError.value = null;

  const alreadySelected = props.form.documents.some(
    document => isContractFileDocument(document) && document.source.contract_file_uid === file.uid
  );

  if (alreadySelected) {
    updateForm(
      props.form.documents.filter(
        document =>
          !isContractFileDocument(document) || document.source.contract_file_uid !== file.uid
      )
    );
    return;
  }

  try {
    const built = await buildDocumentFromContractFile(file, {
      uid: contract.uid,
      name: contract.name,
    });
    updateForm([...props.form.documents, built]);
  } catch (error) {
    contractError.value = extractErrorDetail(error, 'Failed to add the contract file.');
  }
}

/**
 * The contract checkbox. Selecting takes every eligible file and remembers
 * that the whole contract was meant (`full-contract`); deselecting drops all
 * of that contract's files.
 */
async function toggleContract(
  contract: { uid: string; name: string },
  eligibleFiles: ContractManagementContractFileInDb[],
  select: boolean,
  skipped: number
): Promise<void> {
  contractError.value = null;
  skippedNotice.value = null;

  const withoutThisContract = props.form.documents.filter(
    document => !isContractFileDocument(document) || document.source.contract_uid !== contract.uid
  );

  if (!select) {
    updateForm(withoutThisContract, null);
    return;
  }

  try {
    const built = await Promise.all(
      eligibleFiles.map(file =>
        buildDocumentFromContractFile(file, { uid: contract.uid, name: contract.name })
      )
    );

    updateForm([...withoutThisContract, ...built], {
      contract_uid: contract.uid,
      contract_name: contract.name,
      contract_file_uids: eligibleFiles.map(file => file.uid),
      mode: 'full-contract',
    });

    if (skipped > 0) {
      skippedNotice.value = `${skipped} file${skipped === 1 ? '' : 's'} in ${contract.name} ${
        skipped === 1 ? 'does' : 'do'
      } not meet the signing rules and ${skipped === 1 ? 'was' : 'were'} left out.`;
    }
  } catch (error) {
    contractError.value = extractErrorDetail(error, 'Failed to add the contract files.');
  }
}

// ── Vault browser ───────────────────────────────────────────────────────────

/** Every picked file, and a contract taken whole. */
const browserKeys = computed(() => [
  ...props.form.documents.map(document => `file-${document.vault_file.uid}`),
  ...(props.form.contract_selection?.mode === 'full-contract'
    ? [`contract-${props.form.contract_selection.contract_uid}`]
    : []),
]);

/** A request takes contract files from one contract only. */
function otherContractReason(contractUid: string): string | null {
  const locked = lockedContract.value;
  if (!locked || locked.uid === contractUid) return null;
  return `Clear the files from ${locked.name ?? 'the selected contract'} before choosing another contract`;
}

function browserItemState(item: VaultBrowserItem): { disabled: boolean; reason: string } | null {
  let reason: string | null = null;
  if (item.type === 'contract') {
    reason = otherContractReason(item.uid);
  } else if (item.type === 'file') {
    reason =
      (item.contract ? otherContractReason(item.contract.uid) : null) ??
      revisionReason(item.vaultFile?.current_revision);
  }
  return reason ? { disabled: true, reason } : null;
}

/** All of a contract's files, split into what the provider takes and what it doesn't. */
async function loadContractFiles(contractUid: string): Promise<{
  eligible: ContractManagementContractFileInDb[];
  skipped: number;
}> {
  const files = await ContractFilesService.getAllContractFilesForContract(contractUid);
  const eligible = files.filter(file => contractFileDisabledReason(file) === null);
  return { eligible, skipped: files.length - eligible.length };
}

async function onBrowserToggle(item: VaultBrowserItem, select: boolean): Promise<void> {
  if (item.type === 'contract') {
    contractError.value = null;
    try {
      const { eligible, skipped } = select
        ? await loadContractFiles(item.uid)
        : { eligible: [], skipped: 0 };
      await toggleContract({ uid: item.uid, name: item.name }, eligible, select, skipped);
    } catch (error) {
      contractError.value = extractErrorDetail(error, 'Failed to load the contract files.');
    }
    return;
  }
  if (item.type !== 'file') return;
  if (item.contractFile && item.contract) {
    await toggleContractFile(item.contract, item.contractFile);
  } else if (item.vaultFile) {
    toggleVaultFile(item.vaultFile);
  }
}

function documentFromVaultFile(
  vaultFile: VaultFileInDbSlim,
  kind: 'vault-file' | 'upload'
): SignatureUploadDocumentForm | null {
  const revision = vaultFile.current_revision;
  if (!revision) return null;

  return {
    vault_file: vaultFile,
    signing_file_type: SigningFileType.SIGNABLE_DOCUMENT,
    effective_file: buildEffectiveFileFromRevision(revision),
    source: { kind },
  };
}

function toggleVaultFile(file: VaultFileInDbSlim): void {
  const existing = props.form.documents.some(document => document.vault_file.uid === file.uid);
  if (existing) {
    updateForm(props.form.documents.filter(document => document.vault_file.uid !== file.uid));
    return;
  }

  const built = documentFromVaultFile(file, 'vault-file');
  if (built) updateForm([...props.form.documents, built]);
}

// ── Uploads ─────────────────────────────────────────────────────────────────

/**
 * Files uploaded in this wizard, whether or not they are still selected —
 * unticking one keeps its row so it can be ticked again. Seeded from the form
 * so a restored draft's uploads are listed too; a row unticked before a
 * reload is gone from the list but still in the vault.
 */
const uploaded = ref<VaultFileInDb[]>(
  props.form.documents
    .filter(document => document.source?.kind === 'upload')
    .map(document => document.vault_file as VaultFileInDb)
);

const uploadPanel = ref<VaultUploadPanelApi | null>(null);
const uploadReady = ref(false);
const uploadingFiles = ref(false);

/** Upload the panel's files into the folder picked there; each is selected for signing. */
async function uploadPicked(): Promise<void> {
  const panel = uploadPanel.value;
  if (!panel) return;
  uploadingFiles.value = true;
  try {
    const landed = await panel.upload();
    if (!landed) return;
    // The created file comes back without the revision it just gained.
    const vaultFiles = landed.map(({ vaultFile, revision }) => ({
      ...vaultFile,
      current_revision: revision,
    }));
    uploaded.value = [...uploaded.value, ...vaultFiles];
    const built = vaultFiles.flatMap(vaultFile => documentFromVaultFile(vaultFile, 'upload') ?? []);
    if (built.length > 0) updateForm([...props.form.documents, ...built]);
    panel.reset();
  } finally {
    uploadingFiles.value = false;
  }
}

function toggleUploaded(vaultFile: VaultFileInDb, selectedNow: boolean): void {
  if (selectedNow) {
    const built = documentFromVaultFile(vaultFile, 'upload');
    if (built) updateForm([...props.form.documents, built]);
  } else {
    updateForm(props.form.documents.filter(document => document.vault_file.uid !== vaultFile.uid));
  }
}

/** Drops the row and the selection; the vault file itself stays. */
function removeUploaded(vaultFile: VaultFileInDb): void {
  uploaded.value = uploaded.value.filter(file => file.uid !== vaultFile.uid);
  updateForm(props.form.documents.filter(document => document.vault_file.uid !== vaultFile.uid));
}

function isUploadedSelected(vaultFile: VaultFileInDb): boolean {
  return props.form.documents.some(document => document.vault_file.uid === vaultFile.uid);
}

// ── Labels ──────────────────────────────────────────────────────────────────

/** The type select sits in a narrow side column, so it takes the short words. */
const SHORT_FILE_TYPE: Record<SigningFileType, string> = {
  [SigningFileType.SIGNABLE_DOCUMENT]: 'To sign',
  [SigningFileType.ATTACHMENT]: 'Attachment',
};

const signableCount = computed(
  () =>
    props.form.documents.filter(
      document => document.signing_file_type === SigningFileType.SIGNABLE_DOCUMENT
    ).length
);

/** "2 · 1 attachment": how many go out, and how many of them only ride along. */
const selectionCount = computed<string | undefined>(() => {
  const total = props.form.documents.length;
  if (total === 0) return undefined;
  const attachments = total - signableCount.value;
  return attachments === 0
    ? String(total)
    : `${total} · ${attachments} attachment${attachments === 1 ? '' : 's'}`;
});

function vaultFileLabel(vaultFile: VaultFileInDbSlim): string {
  return vaultFile.current_revision?.file_name ?? `Vault file ${vaultFile.uid}`;
}

function documentSourceLabel(document: SignatureUploadDocumentForm): string {
  switch (document.source?.kind) {
    case 'contract-file':
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      return `Contract · ${document.source.contract_name || document.source.contract_uid}`;
    case 'upload':
      return 'Uploaded from device';
    default:
      return 'Vault file';
  }
}

function uploadedMeta(vaultFile: VaultFileInDb): string {
  const revision = vaultFile.current_revision;
  return [
    revision?.size_b == null ? null : formatFileSize(revision.size_b),
    revisionReason(revision),
  ]
    .filter(Boolean)
    .join(' · ');
}

const docSurface = gaiaNeutral.surfaceTint;
const columnRule = gaiaNeutral.borderDivider;

// ── Watchers ────────────────────────────────────────────────────────────────

// The provider decides the limits, so changing it reloads them. The version
// counter drops responses from a provider the user has already moved off.
watch(
  () => props.provider,
  async provider => {
    const requestVersion = fileLimitRequestVersion.value + 1;
    fileLimitRequestVersion.value = requestVersion;
    providerFileLimits.value = null;
    providerFileLimitsError.value = null;
    fileLimitNotice.value = null;

    if (!provider) return;

    providerFileLimitsLoading.value = true;

    try {
      const response = await getFileLimits({ query: { provider } });
      if (fileLimitRequestVersion.value !== requestVersion) return;

      providerFileLimits.value = response.data ?? null;
      if (!providerFileLimits.value) {
        providerFileLimitsError.value = 'No file limits were returned for the selected provider.';
      }
    } catch {
      if (fileLimitRequestVersion.value !== requestVersion) return;
      providerFileLimitsError.value =
        'Failed to load file limits for the selected signing provider.';
    } finally {
      if (fileLimitRequestVersion.value === requestVersion) {
        providerFileLimitsLoading.value = false;
      }
    }
  },
  { immediate: true }
);

// A file that stops qualifying (limits arrived late, or the provider changed)
// is dropped here rather than failing at activation.
watch(
  () =>
    props.form.documents
      .map(document => ({
        uid: document.vault_file.uid,
        reason: limitViolationReason(document.vault_file, document.effective_file),
      }))
      .filter(
        (entry): entry is { uid: string; reason: string } =>
          typeof entry.reason === 'string' && entry.reason.length > 0
      ),
  invalidDocuments => {
    if (invalidDocuments.length === 0) return;

    const invalidUids = new Set(invalidDocuments.map(document => document.uid));
    const nextDocuments = props.form.documents.filter(
      document => !invalidUids.has(document.vault_file.uid)
    );

    if (nextDocuments.length === props.form.documents.length) return;

    const removed = props.form.documents.find(
      document => document.vault_file.uid === invalidDocuments[0].uid
    );

    updateForm(nextDocuments);

    fileLimitNotice.value =
      invalidDocuments.length === 1 && removed
        ? `${vaultFileLabel(removed.vault_file)} was removed: ${invalidDocuments[0].reason}`
        : `${invalidDocuments.length} files were removed because they do not meet the selected provider's file limits.`;
  },
  { deep: true }
);
</script>

<template>
  <!-- The wizard's first step as a workspace: the picker takes the room, and a
       fixed-width panel beside it holds everything the request is — what was
       picked, what each file is for, and the request details (#aside) — so
       none of it waits below the browser to be scrolled to. Below ~840px the
       panel wraps under the picker. -->
  <n-flex :size="24">
    <n-flex vertical :size="12" :wrap="false" :style="{ flex: '1 1 480px', minWidth: 0 }">
      <gaia-banner v-if="providerFileLimitsError" type="error">
        {{ providerFileLimitsError }}
      </gaia-banner>
      <gaia-banner v-if="contractError" type="error">{{ contractError }}</gaia-banner>
      <gaia-banner v-if="fileLimitNotice" type="warning">{{ fileLimitNotice }}</gaia-banner>
      <gaia-banner v-if="skippedNotice" type="warning">{{ skippedNotice }}</gaia-banner>
      <gaia-banner v-if="providerFileLimitsLoading" type="info">
        Loading file limits for the selected signing provider.
      </gaia-banner>

      <!-- Where the files come from. `signing.upload-tabs` is the demo tour's
           handle on this pane; the tabs' own keys let it pick a source. -->
      <gaia-segmented
        data-tour="signing.upload-tabs"
        :value="source"
        :options="sourceTabs"
        @update:value="value => (source = value as Source)"
      />

      <!-- The browser draws its own header and bordered list, so it sits on
           the page bare: a card around it was a border around a border. -->
      <vault-file-browser
        v-if="source !== 'upload'"
        data-tour="signing.file-browser"
        contracts="select"
        controlled
        file-only-selection
        :selected-keys="browserKeys"
        :get-item-selection-state="browserItemState"
        :start-in-contract-uid="props.currentContractUid"
        :style="{ height: '540px' }"
        @toggle="onBrowserToggle"
      />
      <n-flex v-else vertical :size="12" :wrap="false">
        <gaia-vault-upload-panel
          ref="uploadPanel"
          selectable-destination
          :accept="uploadAccept"
          :max-size-b="providerFileLimits?.max_size_b ?? undefined"
          :unpack-archives="false"
          hint="Selected for signing once uploaded"
          @update:ready="uploadReady = $event"
        />
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-upload-button
            size="sm"
            :icon="false"
            :loading="uploadingFiles"
            :disabled="!uploadReady || uploadingFiles"
            @click="uploadPicked"
          />
        </n-flex>
        <n-flex v-if="uploaded.length > 0" vertical :size="8" :wrap="false">
          <gaia-selected-doc-row
            v-for="vaultFile in uploaded"
            :key="vaultFile.uid"
            :name="vaultFileLabel(vaultFile)"
            :source="uploadedMeta(vaultFile)"
            @remove="removeUploaded(vaultFile)"
          >
            <template #actions>
              <gaia-checkbox
                :checked="isUploadedSelected(vaultFile)"
                :disabled="revisionReason(vaultFile.current_revision) !== null"
                aria-label="Include in the request"
                @update:checked="(checked: boolean) => toggleUploaded(vaultFile, checked)"
              />
            </template>
          </gaia-selected-doc-row>
        </n-flex>
      </n-flex>
    </n-flex>

    <!-- An inspector column, not a card: a hairline the full height of the
         row, and its first label on the line of the source switch — a box
         beside a three-part picker had no edge that could line up with it.
         The content inside is sticky, so a long upload list scrolls past it. -->
    <div
      :style="{
        flex: '0 1 340px',
        minWidth: '300px',
        paddingLeft: '24px',
        borderLeft: `1px solid ${columnRule}`,
      }"
    >
      <n-flex vertical :size="18" :wrap="false" :style="{ position: 'sticky', top: 0 }">
        <!-- What is actually going out, and what each file is for. -->
        <n-flex vertical :size="10" :wrap="false">
          <!-- As tall as the switch beside it, so the two rows share a line. -->
          <n-flex align="center" :style="{ minHeight: '28px' }">
            <gaia-section-header :count="selectionCount">In this request</gaia-section-header>
          </n-flex>
          <gaia-text
            v-if="props.form.documents.length === 0"
            size="dense"
            tone="tertiary"
            :style="{ lineHeight: 1.5 }"
          >
            Tick the files to send for signing, or upload one.
          </gaia-text>
          <n-flex v-else vertical :size="8" :wrap="false">
            <template
              v-for="(document, index) in props.form.documents"
              :key="document.vault_file.uid"
            >
              <!-- Two lines so the name gets the panel's full width: the name
                   and its remove, then where it came from and what it is for. -->
              <n-flex
                vertical
                :size="6"
                :wrap="false"
                data-tour="signing.selected-doc"
                :style="{
                  padding: '10px 10px 10px 12px',
                  borderRadius: '8px',
                  background: docSurface,
                }"
              >
                <n-flex align="center" :size="8" :wrap="false">
                  <gaia-text
                    size="dense"
                    weight="semibold"
                    tone="ink"
                    truncate
                    :title="vaultFileLabel(document.vault_file)"
                    :style="{ flex: 1 }"
                  >
                    {{ vaultFileLabel(document.vault_file) }}
                  </gaia-text>
                  <gaia-delete-button
                    intent="remove"
                    :subject="vaultFileLabel(document.vault_file)"
                    @click="removeDocument(index)"
                  />
                </n-flex>
                <n-flex align="center" justify="space-between" :size="8" :wrap="false">
                  <gaia-text size="meta" tone="subtle" truncate>
                    {{ documentSourceLabel(document) }}
                  </gaia-text>
                  <gaia-enum-select
                    :value="document.signing_file_type"
                    :labels="SHORT_FILE_TYPE"
                    size="small"
                    :consistent-menu-width="false"
                    :style="{ width: '112px', flex: 'none' }"
                    @update:value="(value: SigningFileType) => updateDocumentType(index, value)"
                  />
                </n-flex>
              </n-flex>
              <gaia-banner
                v-if="limitViolationReason(document.vault_file, document.effective_file)"
                type="warning"
              >
                {{ limitViolationReason(document.vault_file, document.effective_file) }}
              </gaia-banner>
            </template>
          </n-flex>
        </n-flex>

        <gaia-divider variant="subtle" :style="{ margin: 0 }" />
        <slot name="aside" />
      </n-flex>
    </div>
  </n-flex>
</template>
